import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type LoaderFunctionArgs, useNavigate } from 'react-router'
import { useWindowSize } from 'react-use'
import { useRect, useWindowOn } from 'react-use-rect'
import Logo4dCeramics from '#app/components/logo-4d-ceramics.tsx'
import { EnvironmentSocialGovernance } from '#app/components/marketing/environmental-social-governance.tsx'
import MarketingImage from '#app/components/marketing/image.js'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import Magazine from '#app/components/marketing/magazine.tsx'
import { ProductLink } from '#app/components/marketing/product-link.tsx'
import MarketingSpacer from '#app/components/marketing/spacer.js'
import SplitText from '#app/components/motion/split-text.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { useScroll } from '#app/hooks/use-scroll.js'
import { getLatestPosts } from '#app/utils/blog.server.ts'
import { BASE_URL } from '#app/utils/config.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import {
	i18n,
	type ISupportedLanguage,
	LocalizedLink,
} from '#app/utils/i18n.js'
import {
	generateMetaTranslationLoader,
	i18next,
} from '#app/utils/i18next.server.ts'
import { clamp, mapRange } from '#app/utils/maths.js'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn } from '#app/utils/misc.js'
import routes from '#routes/index.js'
import { type Route } from '../../../../.react-router/types/app/routes/($lang)+/_marketing+/+types'
import { usePublicContext } from '../__public-context'

export async function loader({ request }: LoaderFunctionArgs) {
	const localeOptions = i18n.supportedLngs

	const pathname = new URL(request.url).pathname

	// If the current pathname is not in the list of supported locales, throw a 404 because the page doesn't exist
	if (![...localeOptions.map((locale) => `/${locale}`), '/'].includes(pathname))
		throw new Response('Not found', { status: 404 })

	const latestPosts = await getLatestPosts({
		locale: (await i18next.getLocale(request)) as ISupportedLanguage,
	})

	return {
		latestPosts,
		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'home',
			keys: {
				title: 'title',
				description: 'description',
				canonical: BASE_URL,
			},
		}),
	}
}

export const meta = generateTranslatedMeta

export default function Index({ loaderData }: Route.ComponentProps) {
	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<main>
			<h1 className="sr-only">Sapienstone</h1>

			<div className="mx-auto max-w-[1920px]">
				<HauteCoutureSurfaces />
			</div>
			<TheFourthDimension />

			<DressYourKitchen />
			<DressYourLook />

			<EnvironmentSocialGovernance />

			{loaderData && loaderData.latestPosts && (
				<div className="mx-auto max-w-[1920px]">
					<MarketingSpacer />
					<Magazine latestPosts={loaderData.latestPosts} />
				</div>
			)}
		</main>
	)
}

function DressYourLook() {
	const { t } = useTranslation(['home', 'common'])
	const navigate = useNavigate()

	return (
		<LandingSection
			borderToBorderOnX
			backgroundColor={extendedTheme.colors.brand.dusk.DEFAULT}
		>
			<div
				className={
					'grid grid-cols-12 grid-rows-[repeat(12,7vh)] gap-y-3 py-lg text-brand-dusk-foreground sm:gap-y-5 md:grid-rows-[repeat(12,8vh)] md:py-xxl lg:grid-rows-[repeat(12,8vh)]'
				}
			>
				<MarketingImage
					className={cn(
						'z-0',
						'col-start-1 col-end-12 row-start-1 row-end-5 block',
						'sm:col-start-1 sm:col-end-7 sm:row-span-5 sm:row-start-1 sm:block',
						'md:col-start-1 md:col-end-7 md:row-span-5 md:row-start-1 md:block',
					)}
					src={'/img/homepage/dress-your-look/SAPIENSTONE CASA DECOR2798.webp'}
					alt={'Sapienstone Casa Decor'}
				/>

				<div
					className={cn(
						'z-20 flex flex-col gap-sm',
						'col-start-2 col-end-11 row-start-7 min-[405px]:row-start-8',
						'sm:col-start-8 sm:col-end-12 sm:row-start-2 sm:row-end-6',
						'md:col-start-8 md:col-end-12 md:row-start-2 md:row-end-6',
					)}
				>
					<Text
						as={'h2'}
						size={'h2'}
						className={'-space-y-2 uppercase'}
						weight={'light'}
						type={'serif'}
					>
						<SplitText as={'span'}>DRESS</SplitText>
						<SplitText as={'span'} delay={0.2}>
							YOUR
						</SplitText>
						<SplitText as={'span'} delay={0.4}>
							LOOK
						</SplitText>
					</Text>
					<Text as={'p'} size={'p'} className={'text-balance'}>
						<SplitText as={'span'}>{t('dressYourLook.description')}</SplitText>
					</Text>
					<Button
						asChild
						variant={'link'}
						className={
							'w-fit items-start px-0 text-start text-body-2xs lowercase text-brand-earth xl:text-body-xs'
						}
					>
						<LocalizedLink to={routes.public.landings.dressYourLook.route({})}>
							{t('common:actions.viewMore')}
						</LocalizedLink>
					</Button>
				</div>

				<div
					onClick={() => {
						void navigate(routes.public.landings.dressYourLook.route({}))
					}}
					className={cn(
						'relative z-10 cursor-pointer overflow-hidden',
						'col-start-6 col-end-12 row-start-4 row-end-9',
						'sm:col-start-2 sm:col-end-7 sm:row-start-5 sm:row-end-13',
						'md:col-start-4 md:col-end-9 md:row-start-7 md:row-end-13 md:block',
					)}
				>
					<div
						className={
							'absolute inset-0 z-10 flex select-none flex-col items-center justify-center'
						}
					>
						<Text
							as={'p'}
							size={'h2'}
							className={'-space-y-2 uppercase'}
							weight={'light'}
							type={'serif'}
						>
							<SplitText as={'span'}>DRESS</SplitText>
							<SplitText as={'span'} delay={0.2}>
								CODE
							</SplitText>
						</Text>
						<p className={'text-[9px] font-thin uppercase tracking-[0.2em]'}>
							Jaime Beriestain
						</p>
					</div>
					<MarketingImage
						className={'h-full w-full !scale-150 brightness-50'}
						src={
							'/img/homepage/dress-your-look/20250204_Sapienstone_Jaime_0218_OK_CMYK.webp'
						}
						alt={'Sapienstone Jaime Beriestain'}
					/>
				</div>
			</div>
		</LandingSection>
	)
}

function DressYourKitchen() {
	const { t } = useTranslation(['home', 'common'])
	return (
		<LandingSection
			borderToBorderOnX
			backgroundColor={extendedTheme.colors.brand.mist.DEFAULT}
		>
			<div className="grid grid-cols-12 grid-rows-[repeat(12,7.5vh)] py-lg md:grid-rows-[repeat(20,5vh)] lg:grid-rows-[repeat(20,4.75vh)] xl:py-xxl">
				<MarketingImage
					className={cn(
						'', // common
						'col-start-3 -col-end-1 row-start-1 row-end-5 min-[450px]:row-end-6', // mobile
						'md:col-start-7 md:-col-end-1 md:row-start-1 md:row-end-10', // tablet (md)
						'', // large tablet (lg)
						'', //  laptop (xl)
						'', // desktop (2xl)
					)}
					src="/img/homepage/limestone-brown-counter-detail.webp"
					alt={'Limestone Brown Counter Detail'}
				/>
				<MarketingImage
					className={cn(
						'', // common
						'col-start-6 -col-end-1 row-span-4 row-start-4 min-[450px]:row-start-5', // mobile
						'md:col-span-5 md:col-start-6 md:row-start-11 md:-row-end-1', // tablet (md)
						'', // large tablet (lg)
						'', //  laptop (xl)
						'', // desktop (2xl)
					)}
					src="/img/homepage/pietra-beige-detail.webp"
					alt={'Pietra Beige Detail'}
				/>
				<div
					className={cn(
						'relative flex flex-col gap-sm text-brand-dusk', // common
						'col-start-2 -col-end-2 row-start-8', // mobile
						'md:z-10 md:col-span-4 md:col-start-2 md:row-start-7 md:mr-md md:gap-md md:place-self-auto md:p-0', // tablet (md)
						'', // large tablet (lg)
						'', //  laptop (xl)
						'2xl:gap-lg', // desktop (2xl)
					)}
				>
					<Text
						as={'h2'}
						size={'h2'}
						className={'-space-y-2 uppercase'}
						weight={'light'}
						type={'serif'}
					>
						<SplitText as={'span'}>DRESS</SplitText>
						<SplitText as={'span'} delay={0.2}>
							YOUR
						</SplitText>
						<SplitText as={'span'} delay={0.4}>
							KITCHEN
						</SplitText>
					</Text>
					<Text as={'p'} size={'p'}>
						<SplitText as={'span'}>
							{t('dressYourKitchen.description')}
						</SplitText>
					</Text>

					{/*<Button*/}
					{/*	asChild*/}
					{/*	variant={'link'}*/}
					{/*	className={*/}
					{/*		'w-fit items-start px-0 text-start text-body-2xs lowercase text-brand-earth xl:text-body-xs'*/}
					{/*	}*/}
					{/*>*/}
					{/*	<LocalizedLink to={routes.public.landings.design.route({})}>*/}
					{/*		{t('common:actions.viewMore')}*/}
					{/*	</LocalizedLink>*/}
					{/*</Button>*/}
				</div>
			</div>
		</LandingSection>
	)
}

function HauteCoutureSurfaces() {
	const { t } = useTranslation(['home', 'common'])

	return (
		<LandingSection borderToBorderOnX>
			<div className="grid grid-cols-12 grid-rows-[repeat(11,6vh)] gap-sm px-sm pb-sm pt-md md:grid-rows-[repeat(11,7vh)] md:gap-md lg:grid-rows-[repeat(11,8vh)] lg:gap-0 lg:p-0">
				<MarketingImage
					className={cn(
						'', // common
						'col-start-1 col-end-10 row-start-1 row-end-5', // mobile
						'sm:col-start-1 sm:col-end-8 sm:row-start-1 sm:row-end-7', // tablet (md)
						'lg:col-span-5 lg:col-start-8 lg:row-span-7', // large tablet (lg)
						'xl:col-span-4 xl:col-start-8', //  laptop (xl)
						'2xl:col-span-3 2xl:col-start-9', // desktop (2xl)
					)}
					src="/img/homepage/hands-weaving-in-fine-clothing.webp"
					alt={'Hands Weaving in Fine Clothing'}
				/>

				<MarketingImage
					className={cn(
						'', // common
						'col-start-5 -col-end-1 row-start-3 row-end-7', // mobile
						'sm:col-start-6 sm:-col-end-1 sm:row-start-3 sm:row-end-9', // tablet (md)
						'lg:col-span-6 lg:col-start-1 lg:row-span-10 lg:row-start-2 lg:max-h-none', // large tablet (lg)
						'xl:col-span-5 xl:col-start-2', //  laptop (xl)
						'', // desktop (2xl)
					)}
					src="/img/homepage/scissors-on-sapienstone-ceramic.webp"
					alt={'Scissors on Sapienstone Ceramic'}
				/>

				<div
					className={cn(
						'flex flex-col gap-sm text-brand-dusk', // common
						'col-start-2 -col-end-2 row-start-7 -row-end-1', // mobile
						'sm:col-start-1 sm:col-end-9 sm:row-start-8 sm:-row-end-1 sm:pl-md', // tablet (md)
						'lg:col-span-4 lg:col-start-8 lg:row-start-9 lg:row-end-12 lg:gap-md lg:place-self-auto', // large tablet (lg)
						'xl:col-start-8', //  laptop (xl)
						'2xl:gap-lg', // desktop (2xl)
					)}
				>
					<Text
						as={'h2'}
						size={'h2'}
						className={'-space-y-2 uppercase'}
						weight={'light'}
						type={'serif'}
					>
						<SplitText as={'span'}>HAUTE</SplitText>
						<SplitText as={'span'} delay={0.2}>
							COUTURE
						</SplitText>
						<SplitText as={'span'} delay={0.4}>
							SURFACES
						</SplitText>
					</Text>
					<Text as={'p'} size={'p'}>
						<SplitText as={'span'}>
							{t('hauteCoutureSurfaces.description')}
						</SplitText>
					</Text>

					<Button
						asChild
						variant={'link'}
						className={
							'w-fit items-start px-0 text-start text-body-2xs lowercase text-brand-earth xl:text-body-xs'
						}
					>
						<LocalizedLink to={routes.public.about.route({})}>
							{t('common:actions.viewMore')}
						</LocalizedLink>
					</Button>
				</div>
			</div>
		</LandingSection>
	)
}

/* 
cn(
	'', // common
	'', // mobile
	'', // tablet (md)
	'', // large tablet (lg)
	'', //  laptop (xl)
	'', // desktop (2xl)
)
*/

function TheFourthDimension() {
	const { t } = useTranslation(['home', 'common'])
	const [top, setTop] = useState<number | null>(null)
	const [height, setHeight] = useState<number | null>(null)
	const [progress, setProgress] = useState(0)
	const [rectRef, revalidate] = useRect((rect) => {
		setTop(rect.top)
		setHeight(rect.height)
	})
	useWindowOn('scroll', () => revalidate())
	const { height: windowHeight } = useWindowSize()

	useScroll(({ scroll }) => {
		if (!top || !height) return

		const start = top + scroll
		const end = top + scroll + height - windowHeight

		const p1 = clamp(0, mapRange(start, end, scroll, 0, 1), 1)

		setProgress(p1)
	})

	return (
		// Wrapper
		<div
			ref={rectRef}
			className="relative h-[180vh] overflow-visible sm:h-[250vh]"
		>
			{/* Inner */}
			<div className="sticky top-0">
				<div className="relative flex h-screen items-center justify-center">
					<img
						style={{
							// Duplicate the progress of the image to make it fade in faster
							opacity: (progress - 0.1) * 2,
						}}
						className={cn(
							'absolute bottom-0 left-0 right-0 top-0 z-[-1] h-full w-full object-cover',
						)}
						src="/img/4D/calacatta-4d-veins.webp"
						alt="Calacatta veins 4D example"
					/>
					<h2
						className={cn(
							'z-10 m-0 mx-auto flex h-[100%] w-full items-center self-center',
							"bg-[url('/img/4D/calacatta-4d-veins.webp')] bg-cover bg-clip-text bg-center bg-no-repeat bg-origin-content",
							'hyphens-auto break-words font-serif text-[15vw] font-light uppercase leading-[100%] text-transparent',
						)}
					>
						<span className="px-sm">
							The Fourth
							<br /> Dimension
						</span>
					</h2>
					<div
						className="absolute inset-0 z-20"
						style={{
							opacity: (progress - 0.6) * 5,
						}}
					>
						<div className="relative flex h-full w-full items-center justify-center text-white">
							<div className="mx-md flex flex-col-reverse items-center gap-md lg:ml-[30%] lg:flex-row lg:gap-0">
								<ProductLink
									className={
										'relative z-10 flex items-center justify-center gap-sm self-center lg:translate-y-14'
									}
									link={routes.public.landings.fourthDimension.route({})}
									children={t('common:actions.checkOurProduct')
										.split(' ')
										.map((item: string) => (
											<span key={item}>
												{item}
												<br />
											</span>
										))}
								/>
								<div className="z-0 flex flex-col gap-xxs md:max-w-[450px] md:gap-md lg:-ml-[100px] lg:gap-lg">
									<Logo4dCeramics className={'max-w-[270px]'} />
									<Text as={'p'} size={'p'} weight={'light'}>
										{t('theFourthDimension.description')}
									</Text>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
